/**
 * This file is based on this article regarding Accessible image carousel https://www.aleksandrhovhannisyan.com/blog/image-carousel-tutorial/#html-a-semantic-carousel
 */

/**
 * Returns `true` if the given element is in a horizontal RTL writing mode.
 * @param {HTMLElement} element
 */
const isRtl = (element) => window.getComputedStyle(element).direction === 'rtl';

/**
 * Returns the distance from the starting edge of the viewport to the given focal point on the element.
 * @param {HTMLElement} element
 * @param {'start'|'center'|'end'} [focalPoint]
 */
const getDistanceToFocalPoint = (element, focalPoint = 'center') => {
  const isHorizontalRtl = isRtl(element);
  const documentWidth = document.documentElement.clientWidth;
  const rect = element.getBoundingClientRect();
  switch (focalPoint) {
    case 'start':
      return isHorizontalRtl ? documentWidth - rect.right : rect.left;
    case 'end':
      return isHorizontalRtl ? documentWidth - rect.left : rect.right;
    case 'center':
    default: {
      const centerFromLeft = rect.left + rect.width / 2;
      return isHorizontalRtl ? documentWidth - centerFromLeft : centerFromLeft;
    }
  }
};

/**
 * This class handles the scrolling of the carousel and the enabling and disabling of buttons.
 * @param {HTMLElement} root the root of carousel
 * @param {string} [type] determines which type of scroll to use
 * @param {number} [distance] scroll distance when using distance type
 * @param {string} [class] generated control container class
 * @param {HTMLElement} [controlContainer] passed control container element
 */
export default class Carousel {
  constructor(props) {
    this.navigateToNextItem = this.navigateToNextItem.bind(this);

    this.carousel = props.root;
    this.scrollType = props.type;
    this.scrollDistance = props.distance;
    this.controlContainer = props.controlContainer;
    this.scrollContainer = this.carousel.querySelector('[role="region"][tabindex="0"]');
    this.mediaList = this.scrollContainer.querySelector('[role="list"]');

    if (!this.controlContainer) {      
      const controls = document.createElement('ol');
      controls.setAttribute('role', 'list');
      controls.classList.add(props.class, 'controls');
      controls.setAttribute('aria-label', 'Navigation controls');
      this.navControlPrevious = this.createNavButton('start', props.class, controls);
      this.navControlNext = this.createNavButton('end', props.class, controls);
      this.carousel.appendChild(controls);
    } else {
      this.navControlPrevious = this.controlContainer.querySelector('.start');
      this.navControlPrevious.addEventListener('click', () => this.navigateToNextItem('start'));
      this.navControlNext = this.controlContainer.querySelector('.end');
      this.navControlNext.addEventListener('click', () => this.navigateToNextItem('end'));
    }

    this.handleCarouselScroll = this.handleCarouselScroll.bind(this);
    this.scrollContainer.addEventListener('scroll', this.handleCarouselScroll);
    this.handleCarouselScroll();
  }

  // Handles creation of default navigation button if none are passed.
  createNavButton (direction, buttonClass, controls) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    const icon = document.createElement('i');
    icon.classList.add('arrow');
    button.classList.add(buttonClass, direction);
    button.setAttribute('aria-label', `button ${direction}`);
    button.append(icon);
    button.addEventListener('click', () => this.navigateToNextItem(direction));
    li.appendChild(button);
    controls.appendChild(li);
    return button;
  };

  // Disables or enables the controls depending on scroll left on the scrollbar
  handleCarouselScroll() {
    const scrollLeft = Math.abs(this.scrollContainer.scrollLeft);
    const width = this.scrollContainer.clientWidth + 1;
    const isAtStart = Math.floor(scrollLeft) === 0;
    const isAtEnd = Math.ceil(width + scrollLeft) >= this.scrollContainer.scrollWidth;
    this.navControlPrevious.disabled = isAtStart;
    this.navControlNext.disabled = isAtEnd;
  }

  // Scrolls by the center of the closest image depending on scroll direction.
  scrollByFocalPoint(direction) {
    let mediaItems = Array.from(this.mediaList.querySelectorAll(':scope > *'));
    mediaItems = direction === 'start' ? mediaItems.reverse() : mediaItems;

    const scrollContainerCenter = getDistanceToFocalPoint(this.scrollContainer, 'center');
    let targetFocalPoint;
    for (const mediaItem of mediaItems) {
      let focalPoint = window.getComputedStyle(mediaItem).scrollSnapAlign;
      if (focalPoint === 'none') {
        focalPoint = 'center';
      }
      const distanceToItem = getDistanceToFocalPoint(mediaItem, focalPoint);
      if (
        (direction === 'start' && distanceToItem + 1 < scrollContainerCenter) ||
        (direction === 'end' && distanceToItem - scrollContainerCenter > 1)
      ) {
        targetFocalPoint = distanceToItem;
        break;
      }
    }

    // This should never happen, but it doesn't hurt to check
    if (typeof targetFocalPoint === 'undefined') return;
    // RTL flips the direction
    const sign = isRtl(this.carousel) ? -1 : 1;
    const scrollAmount = sign * (targetFocalPoint - scrollContainerCenter);
    this.scrollContainer.scrollBy({ left: scrollAmount });
  }

  // Scroll the carousel by a given distance.
  scrollByDistance(direction, distance) {
    if (direction === 'start') {
      this.scrollContainer.scrollBy({left: -distance, behaviour:'instant'});
    } else if(direction === 'end') {
      this.scrollContainer.scrollBy({left: distance, behaviour:'instant'});
    }
  }

  // Handles the click event on the control buttons based on passed scrollType.
  navigateToNextItem(direction) {
    switch (this.scrollType) {
      case 'distance': 
        if (this.scrollDistance) {
          this.scrollByDistance(direction, this.scrollDistance);
          break;
        }
      default:
        this.scrollByFocalPoint(direction);
   }
  }
}
